import { nanoid } from 'nanoid';

import type { Locale } from '@change-corgi/config/locales';
import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import { NotFoundError, RedirectError } from 'src/shared/error';

import type { ImpactStoryDataQuery, ImpactStoryDataQueryVariables } from './api.graphql';
import { ImpactStoryData } from './api.graphql';

type ImpactStoryType = NonNullable<
	NonNullable<NonNullable<ImpactStoryDataQuery['featureConfigs']['capybaraImpactStory']>[Locale]>['impactStories'][0]
>;
type GuidesListType = ReadonlyArray<{
	title: string;
	guideLink: string;
}>;

export type ImpactStoryPageData = {
	petition: NonNullable<ImpactStoryDataQuery['petition']>;
	featureConfig: {
		story: ImpactStoryType;
		guides: GuidesListType;
		guidesEnabled: boolean;
	};
	aiSapId: string;
};

export async function getImpactStoryPageData(
	{ gql: { fetch } }: UtilityContext,
	slug: string,
	locale: string,
): Promise<ImpactStoryPageData> {
	const { data } = await fetch<ImpactStoryDataQuery, ImpactStoryDataQueryVariables>({
		query: ImpactStoryData,
		variables: {
			slugOrId: slug,
		},
		rejectOnError: true,
	});

	if (!data.petition) {
		throw new NotFoundError();
	}

	const storyLocaleConfig = data.featureConfigs.capybaraImpactStory[locale as Locale];

	if (!storyLocaleConfig?.enabled || !storyLocaleConfig.impactStories[slug]) {
		throw new RedirectError({ url: `/p/${encodeURIComponent(slug)}`, reason: 'impact-story-not-available' });
	}

	const guidesLocaleConfig = data.featureConfigs.capybaraHomepageV3?.perLocaleConfig?.[locale as Locale];

	const guidesEnabled = guidesLocaleConfig?.layout.howTo === true;
	const guides = guidesLocaleConfig?.tips ?? [];

	return {
		petition: data.petition,
		featureConfig: {
			story: storyLocaleConfig.impactStories[slug],
			guides,
			guidesEnabled,
		},
		aiSapId: nanoid(),
	};
}
